/**
 * Wave 27 — 10 New City Agency Pages
 * Bordeaux 🇫🇷, Hannover 🇩🇪, Wrocław 🇵🇱, Ljubljana 🇸🇮, Nicosia 🇨🇾,
 * Hyderabad Hub2 🇮🇳, Bogotá Hub2 🇨🇴, Abuja 🇳🇬, Maputo 🇲🇿, Muscat Hub2 🇴🇲
 */
import type { CityAgencyData } from './city-agency-pages';

const sharedServices = [
  { name: 'Custom SaaS Development', description: 'End-to-end SaaS product development: architecture, development, deployment, and scaling.' },
  { name: 'Web Application Development', description: 'Enterprise web applications, portals, dashboards, and internal tools with modern stack.' },
  { name: 'Mobile App Development', description: 'Native iOS/Android and cross-platform apps with React Native and Flutter.' },
  { name: 'AI & Machine Learning', description: 'AI-powered solutions: NLP, computer vision, predictive analytics, and LLM integration.' },
  { name: 'Cloud & DevOps', description: 'AWS, Azure, GCP infrastructure. CI/CD pipelines, containerization, and managed services.' },
  { name: 'Staff Augmentation', description: 'Dedicated developers, QA engineers, DevOps specialists integrated into your team.' },
  { name: 'UI/UX Design', description: 'User research, wireframing, prototyping, and design systems with Figma-first approach.' },
  { name: 'Cybersecurity', description: 'Penetration testing, security audits, SOC operations, and compliance consulting.' },
];

const sharedIndustries = [
  { name: 'FinTech & Banking', description: 'Payment platforms, trading systems, compliance tools with PCI-DSS and SOC 2.' },
  { name: 'E-commerce & Retail', description: 'Online stores, marketplace platforms, inventory management solutions.' },
  { name: 'Healthcare & MedTech', description: 'Patient portals, telemedicine, EHR integrations, HIPAA-compliant apps.' },
  { name: 'SaaS & Technology', description: 'Product development and modernization for SaaS companies seed to Series C+.' },
  { name: 'Real Estate & PropTech', description: 'Property management platforms, virtual tours, tenant portals.' },
  { name: 'Logistics & Supply Chain', description: 'Fleet management, route optimization, warehouse management platforms.' },
];

export const CITY_AGENCY_PAGES_WAVE27: Record<string, CityAgencyData> = {

  bordeaux: {
    slug: 'software-development-agency-bordeaux',
    cityName: 'Bordeaux',
    countryName: 'France',
    flag: '🇫🇷',
    timezone: 'CET (GMT+1)',
    currency: 'EUR',
    title: {
      en: 'Software Development Agency Bordeaux | Call IT Dev',
      fr: 'Agence de Développement Informatique Bordeaux | Call IT Dev',
      es: 'Agencia Desarrollo Software Burdeos | Call IT Dev',
      de: 'Softwareentwicklung Agentur Bordeaux | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Bordeaux. Custom SaaS, web, mobile, AI. French-speaking engineers, CET timezone, from €25/hr. 60% savings.',
      fr: 'Agence de développement informatique pour Bordeaux. SaaS, web, mobile, IA. Ingénieurs francophones, fuseau CET, dès 25 €/h. 60% d\'économies.',
      es: 'Agencia de desarrollo de software para Burdeos. SaaS, web, móvil, IA. Desde 25 €/h. 60% ahorro.',
      de: 'Softwareentwicklungsagentur für Bordeaux. SaaS, Web, Mobile, KI. Ab 25 €/h. 60% Ersparnis.',
    },
    keywords: ['software development agency Bordeaux', 'agence développement informatique Bordeaux', 'nearshore development Bordeaux'],
    heroTitle: {
      en: 'Software Development Agency for Bordeaux',
      fr: 'Agence de Développement Informatique pour Bordeaux',
      es: 'Agencia de Desarrollo de Software para Burdeos',
      de: 'Softwareentwicklung Agentur für Bordeaux',
    },
    heroSubtitle: {
      en: 'French-speaking engineers at nearshore rates. CET timezone, 60% savings vs local Bordeaux agencies.',
      fr: 'Ingénieurs francophones à tarifs nearshore. Fuseau CET, 60% d\'économies vs agences bordelaises.',
      es: 'Ingenieros francófonos a precios nearshore. 60% ahorro vs agencias de Burdeos.',
      de: 'Französischsprachige Ingenieure zu Nearshore-Preisen. 60% Ersparnis.',
    },
    whyTitle: {
      en: 'Why Bordeaux Companies Choose Call IT Dev',
      fr: 'Pourquoi les Entreprises Bordelaises Choisissent Call IT Dev',
      es: 'Por Qué las Empresas de Burdeos Eligen Call IT Dev',
      de: 'Warum Bordeauxer Unternehmen Call IT Dev Wählen',
    },
    whyReasons: [
      { icon: '🗣️', title: 'Native French Engineers', description: 'Seamless French-language communication for Bordeaux teams.' },
      { icon: '⏰', title: 'CET Timezone', description: 'Same timezone as Bordeaux. Real-time collaboration.' },
      { icon: '💰', title: '60% Cost Savings', description: 'Senior devs at €25-40/hr vs €70-100/hr locally.' },
      { icon: '✈️', title: 'Direct Flights', description: 'Casablanca-Bordeaux direct connections for onsite visits.' },
      { icon: '🏗️', title: 'Tech Ecosystem', description: 'Supporting Bordeaux\'s growing digital and wine-tech ecosystem.' },
      { icon: '🚀', title: 'Fast Scaling', description: 'Dedicated team in 2 weeks. No local recruitment hassle.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Why outsource from Bordeaux?', answer: 'Bordeaux\'s tech scene is growing but talent is scarce. Our nearshore model provides French-speaking engineers at 60% savings.' },
      { question: 'What technologies?', answer: 'React, TypeScript, Node.js, Python, Java, AWS, Azure, GCP, Docker, Kubernetes.' },
      { question: 'How fast to start?', answer: '2-week onboarding. CVs within 5 business days.' },
      { question: 'RGPD compliant?', answer: 'Yes. ISO 27001 certified, RGPD/GDPR compliant.' },
      { question: 'Can you integrate with our team?', answer: 'Yes. Full Jira, Slack, Git integration. Same standups, same sprints.' },
      { question: 'Engagement models?', answer: 'Time & materials, fixed-price, and dedicated team models.' },
    ],
    localRate: '€70-100',
    ourRate: '€25-40',
    savings: '60%+',
  },

  hannover: {
    slug: 'software-development-agency-hannover',
    cityName: 'Hannover',
    countryName: 'Germany',
    flag: '🇩🇪',
    timezone: 'CET (GMT+1)',
    currency: 'EUR',
    title: {
      en: 'Software Development Agency Hannover | Call IT Dev',
      fr: 'Agence de Développement Informatique Hanovre | Call IT Dev',
      de: 'Softwareentwicklung Agentur Hannover | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Hannover. Custom SaaS, web, mobile, AI. German-speaking engineers, CET timezone, from €30/hr. 55% savings.',
      fr: 'Agence de développement pour Hanovre. SaaS, web, mobile, IA. Dès 30 €/h. 55% d\'économies.',
      de: 'Softwareentwicklungsagentur für Hannover. SaaS, Web, Mobile, KI. Deutschsprachige Ingenieure, ab 30 €/h. 55% Ersparnis.',
    },
    keywords: ['software development agency Hannover', 'Softwareentwicklung Hannover', 'IT outsourcing Hannover'],
    heroTitle: {
      en: 'Software Development Agency for Hannover',
      fr: 'Agence de Développement Informatique pour Hanovre',
      de: 'Softwareentwicklung Agentur für Hannover',
    },
    heroSubtitle: {
      en: 'German-speaking engineers at nearshore rates. CET timezone, 55% savings vs Hannover agencies.',
      fr: 'Ingénieurs germanophones à tarifs nearshore. 55% d\'économies.',
      de: 'Deutschsprachige Ingenieure zu Nearshore-Preisen. CET-Zeitzone, 55% Ersparnis.',
    },
    whyTitle: {
      en: 'Why Hannover Companies Choose Call IT Dev',
      fr: 'Pourquoi les Entreprises de Hanovre Choisissent Call IT Dev',
      de: 'Warum Hannoveraner Unternehmen Call IT Dev Wählen',
    },
    whyReasons: [
      { icon: '🗣️', title: 'German-Speaking', description: 'Fluent German engineers for seamless Niedersachsen collaboration.' },
      { icon: '⏰', title: 'CET Timezone', description: 'Identical working hours for real-time standups.' },
      { icon: '💰', title: '55% Savings', description: 'Senior devs at €30-45/hr vs €80-120/hr in Hannover.' },
      { icon: '🏭', title: 'Industrial IoT', description: 'Supporting Hannover\'s Industry 4.0 and Messe-driven innovation.' },
      { icon: '🛡️', title: 'DSGVO Compliant', description: 'Full German data protection compliance.' },
      { icon: '🚀', title: 'Rapid Scaling', description: 'Team operational in 2 weeks.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Why outsource from Hannover?', answer: 'Hannover faces IT talent shortages despite being a major industrial hub. Our model delivers German-speaking engineers at 55% savings.' },
      { question: 'German speakers?', answer: 'Yes. B2-C1+ German fluency for all assigned engineers.' },
      { question: 'DSGVO?', answer: 'ISO 27001 certified, DSGVO/GDPR compliant. AVV signed.' },
      { question: 'Industries?', answer: 'Industry 4.0, automotive, insurance, logistics, and enterprise SaaS.' },
      { question: 'How fast?', answer: '2-week onboarding. CVs in 5 business days.' },
      { question: 'Team size?', answer: 'Start with 2, scale to 30+. Flexible models.' },
    ],
    localRate: '€80-120',
    ourRate: '€30-45',
    savings: '55%+',
  },

  wroclaw: {
    slug: 'software-development-agency-wroclaw',
    cityName: 'Wrocław',
    countryName: 'Poland',
    flag: '🇵🇱',
    timezone: 'CET (GMT+1)',
    currency: 'PLN/EUR',
    title: {
      en: 'Software Development Agency Wrocław | Call IT Dev',
      fr: 'Agence de Développement Informatique Wrocław | Call IT Dev',
      de: 'Softwareentwicklung Agentur Wrocław | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Wrocław. Custom SaaS, web, mobile, AI. CET timezone, from €25/hr. 50% savings.',
      fr: 'Agence de développement pour Wrocław. SaaS, web, mobile, IA. Dès 25 €/h. 50% d\'économies.',
      de: 'Softwareentwicklungsagentur für Wrocław. SaaS, Web, Mobile, KI. Ab 25 €/h. 50% Ersparnis.',
    },
    keywords: ['software development agency Wrocław', 'IT outsourcing Wrocław', 'nearshore development Wrocław'],
    heroTitle: {
      en: 'Software Development Agency for Wrocław',
      fr: 'Agence de Développement Informatique pour Wrocław',
      de: 'Softwareentwicklung Agentur für Wrocław',
    },
    heroSubtitle: {
      en: 'Skilled engineers at nearshore rates. CET timezone, 50% savings vs Wrocław agencies.',
      fr: 'Ingénieurs qualifiés à tarifs nearshore. 50% d\'économies vs agences de Wrocław.',
      de: 'Qualifizierte Ingenieure zu Nearshore-Preisen. 50% Ersparnis.',
    },
    whyTitle: {
      en: 'Why Wrocław Companies Choose Call IT Dev',
      fr: 'Pourquoi les Entreprises de Wrocław Choisissent Call IT Dev',
      de: 'Warum Breslauer Unternehmen Call IT Dev Wählen',
    },
    whyReasons: [
      { icon: '🎓', title: 'Top Talent', description: 'Access engineers beyond Wrocław\'s competitive local market.' },
      { icon: '⏰', title: 'CET Timezone', description: 'Same timezone for seamless collaboration.' },
      { icon: '💰', title: '50% Savings', description: 'Senior devs at €25-35/hr vs €50-70/hr locally.' },
      { icon: '🌍', title: 'Multilingual', description: 'Engineers fluent in English, French, Arabic.' },
      { icon: '🛡️', title: 'GDPR Compliant', description: 'ISO 27001 certified, full EU compliance.' },
      { icon: '🚀', title: 'Scale Fast', description: 'Dedicated team in 2 weeks.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Why outsource from Wrocław?', answer: 'Wrocław is Poland\'s top tech city but faces talent wars. Our nearshore model provides 50% savings with CET alignment.' },
      { question: 'Technologies?', answer: 'React, TypeScript, Node.js, Python, Java, AWS, Azure, Docker, Kubernetes.' },
      { question: 'How fast?', answer: '2-week onboarding. CVs in 5 business days.' },
      { question: 'RODO/GDPR?', answer: 'Yes. ISO 27001 certified, GDPR/RODO compliant.' },
      { question: 'Engagement models?', answer: 'T&M, fixed-price, dedicated team.' },
      { question: 'Minimum size?', answer: 'Start with 2 developers, scale to 30+.' },
    ],
    localRate: '€50-70',
    ourRate: '€25-35',
    savings: '50%+',
  },

  ljubljana: {
    slug: 'software-development-agency-ljubljana',
    cityName: 'Ljubljana',
    countryName: 'Slovenia',
    flag: '🇸🇮',
    timezone: 'CET (GMT+1)',
    currency: 'EUR',
    title: {
      en: 'Software Development Agency Ljubljana | Call IT Dev',
      fr: 'Agence de Développement Informatique Ljubljana | Call IT Dev',
      de: 'Softwareentwicklung Agentur Ljubljana | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Ljubljana. Custom SaaS, web, mobile, AI. CET timezone, from €25/hr. 45% savings.',
      fr: 'Agence de développement pour Ljubljana. SaaS, web, mobile, IA. Dès 25 €/h. 45% d\'économies.',
      de: 'Softwareentwicklungsagentur für Ljubljana. SaaS, Web, Mobile, KI. Ab 25 €/h. 45% Ersparnis.',
    },
    keywords: ['software development agency Ljubljana', 'IT outsourcing Ljubljana', 'nearshore development Slovenia'],
    heroTitle: {
      en: 'Software Development Agency for Ljubljana',
      fr: 'Agence de Développement Informatique pour Ljubljana',
      de: 'Softwareentwicklung Agentur für Ljubljana',
    },
    heroSubtitle: {
      en: 'Expert engineers for Slovenia\'s tech market. CET timezone, 45% savings.',
      fr: 'Ingénieurs experts pour le marché tech slovène. 45% d\'économies.',
      de: 'Experten-Ingenieure für Sloweniens Tech-Markt. 45% Ersparnis.',
    },
    whyTitle: {
      en: 'Why Ljubljana Companies Choose Call IT Dev',
      fr: 'Pourquoi les Entreprises de Ljubljana Choisissent Call IT Dev',
      de: 'Warum Laibacher Unternehmen Call IT Dev Wählen',
    },
    whyReasons: [
      { icon: '🎓', title: 'Talent Access', description: 'Overcome Ljubljana\'s small but competitive IT market.' },
      { icon: '⏰', title: 'CET Timezone', description: 'Identical working hours.' },
      { icon: '💰', title: '45% Savings', description: 'Senior devs at €25-38/hr vs €50-70/hr locally.' },
      { icon: '🌍', title: 'EU Standards', description: 'GDPR compliant, ISO 27001 certified.' },
      { icon: '🚀', title: 'Quick Start', description: 'Team operational in 2 weeks.' },
      { icon: '🛡️', title: 'Security First', description: 'Enterprise-grade data protection.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Why outsource from Ljubljana?', answer: 'Slovenia\'s tiny market makes hiring hard. Our nearshore model extends your team at 45% savings.' },
      { question: 'Technologies?', answer: 'React, TypeScript, Node.js, Python, Java, AWS, Azure, Docker, Kubernetes.' },
      { question: 'GDPR?', answer: 'Yes. ISO 27001 certified, fully GDPR compliant.' },
      { question: 'How fast?', answer: '2-week onboarding.' },
      { question: 'Languages?', answer: 'English, French, Arabic. German speakers available.' },
      { question: 'Models?', answer: 'T&M, fixed-price, dedicated team.' },
    ],
    localRate: '€50-70',
    ourRate: '€25-38',
    savings: '45%+',
  },

  nicosia: {
    slug: 'software-development-agency-nicosia',
    cityName: 'Nicosia',
    countryName: 'Cyprus',
    flag: '🇨🇾',
    timezone: 'EET (GMT+2)',
    currency: 'EUR',
    title: {
      en: 'Software Development Agency Nicosia | Call IT Dev',
      fr: 'Agence de Développement Informatique Nicosie | Call IT Dev',
      de: 'Softwareentwicklung Agentur Nikosia | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Nicosia. Custom SaaS, web, mobile, AI, FinTech. EET timezone, from €25/hr. 50% savings.',
      fr: 'Agence de développement pour Nicosie. SaaS, web, mobile, IA, FinTech. Dès 25 €/h. 50% d\'économies.',
      de: 'Softwareentwicklungsagentur für Nikosia. SaaS, Web, Mobile, KI, FinTech. Ab 25 €/h. 50% Ersparnis.',
    },
    keywords: ['software development agency Nicosia', 'IT outsourcing Cyprus', 'fintech development Nicosia'],
    heroTitle: {
      en: 'Software Development Agency for Nicosia',
      fr: 'Agence de Développement Informatique pour Nicosie',
      de: 'Softwareentwicklung Agentur für Nikosia',
    },
    heroSubtitle: {
      en: 'Expert engineers for Cyprus\'s FinTech hub. EET timezone, 50% savings.',
      fr: 'Ingénieurs experts pour le hub FinTech chypriote. 50% d\'économies.',
      de: 'Experten-Ingenieure für Zyperns FinTech-Hub. 50% Ersparnis.',
    },
    whyTitle: {
      en: 'Why Nicosia Companies Choose Call IT Dev',
      fr: 'Pourquoi les Entreprises de Nicosie Choisissent Call IT Dev',
      de: 'Warum Nikosianische Unternehmen Call IT Dev Wählen',
    },
    whyReasons: [
      { icon: '🏦', title: 'FinTech Expertise', description: 'Supporting Cyprus\'s growing financial services and crypto sector.' },
      { icon: '⏰', title: 'EET Timezone', description: '1-hour difference. Strong daily overlap.' },
      { icon: '💰', title: '50% Savings', description: 'Senior devs at €25-40/hr vs €55-85/hr in Nicosia.' },
      { icon: '🗣️', title: 'English/Greek Support', description: 'Engineers fluent in English for Cyprus market.' },
      { icon: '🛡️', title: 'EU Compliant', description: 'GDPR, ISO 27001, CySEC-aware.' },
      { icon: '🚀', title: 'Fast Start', description: 'Dedicated team in 2 weeks.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Why outsource from Nicosia?', answer: 'Cyprus has a booming FinTech scene but a small talent pool. Our model provides 50% savings with EET alignment.' },
      { question: 'FinTech experience?', answer: 'Yes. Payment gateways, crypto platforms, forex, CySEC compliance.' },
      { question: 'GDPR?', answer: 'Yes. ISO 27001 certified, fully GDPR compliant.' },
      { question: 'Technologies?', answer: 'React, TypeScript, Node.js, Python, Java, AWS, Azure, Docker, Kubernetes.' },
      { question: 'How fast?', answer: '2-week onboarding. CVs in 5 business days.' },
      { question: 'Models?', answer: 'T&M, fixed-price, dedicated team.' },
    ],
    localRate: '€55-85',
    ourRate: '€25-40',
    savings: '50%+',
  },

  hyderabad_hub2: {
    slug: 'software-development-agency-hyderabad-hub2',
    cityName: 'Hyderabad',
    countryName: 'India',
    flag: '🇮🇳',
    timezone: 'IST (GMT+5:30)',
    currency: 'INR/USD',
    title: {
      en: 'Software Development Agency Hyderabad | Call IT Dev',
      fr: 'Agence de Développement Informatique Hyderabad | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Hyderabad. Custom SaaS, web, mobile, AI. From $20/hr. 70% savings vs US/UK rates.',
      fr: 'Agence de développement pour Hyderabad. SaaS, web, mobile, IA. Dès 20 $/h. 70% d\'économies.',
    },
    keywords: ['software development agency Hyderabad', 'IT outsourcing Hyderabad', 'software company Hyderabad'],
    heroTitle: {
      en: 'Software Development Agency for Hyderabad',
      fr: 'Agence de Développement Informatique pour Hyderabad',
    },
    heroSubtitle: {
      en: 'Expert engineers for Hyderabad\'s HITEC City ecosystem. 70% savings vs US/UK rates.',
      fr: 'Ingénieurs experts pour l\'écosystème HITEC City d\'Hyderabad. 70% d\'économies.',
    },
    whyTitle: {
      en: 'Why Hyderabad Companies Choose Call IT Dev',
      fr: 'Pourquoi les Entreprises d\'Hyderabad Choisissent Call IT Dev',
    },
    whyReasons: [
      { icon: '🏙️', title: 'HITEC City', description: 'Supporting Hyderabad\'s massive IT corridor with scalable teams.' },
      { icon: '💰', title: '70% Savings', description: 'Competitive rates vs US/UK agencies. Premium quality.' },
      { icon: '🌍', title: 'Global Standards', description: 'ISO 27001 certified. International processes.' },
      { icon: '⏰', title: 'Flexible Hours', description: 'Overlap hours for daily collaboration.' },
      { icon: '🚀', title: 'Fast Scaling', description: 'Team in 2 weeks. Scale to 50+.' },
      { icon: '🛡️', title: 'Security', description: 'Enterprise-grade data protection.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Why outsource from Hyderabad?', answer: 'Hyderabad is India\'s #2 IT hub but faces intense talent competition. Our model provides skilled engineers at competitive rates.' },
      { question: 'Technologies?', answer: 'React, TypeScript, Node.js, Python, Java, AWS, Azure, GCP, Docker, Kubernetes, TensorFlow.' },
      { question: 'How fast?', answer: '2-week onboarding.' },
      { question: 'Security?', answer: 'ISO 27001 certified, GDPR compliant.' },
      { question: 'Models?', answer: 'T&M, fixed-price, dedicated team.' },
      { question: 'Scale?', answer: 'Start with 2 devs, scale to 50+.' },
    ],
    localRate: '$40-80',
    ourRate: '$20-35',
    savings: '70%+',
  },

  bogota_hub2: {
    slug: 'software-development-agency-bogota-hub2',
    cityName: 'Bogotá',
    countryName: 'Colombia',
    flag: '🇨🇴',
    timezone: 'COT (GMT-5)',
    currency: 'COP/USD',
    title: {
      en: 'Software Development Agency Bogotá Hub | Call IT Dev',
      fr: 'Agence de Développement Informatique Bogotá Hub | Call IT Dev',
      es: 'Agencia Desarrollo Software Bogotá Hub | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Bogotá. Custom SaaS, web, mobile, AI. Bilingual ES/EN engineers, from $22/hr. 65% savings.',
      fr: 'Agence de développement pour Bogotá. SaaS, web, mobile, IA. Dès 22 $/h. 65% d\'économies.',
      es: 'Agencia de desarrollo para Bogotá. SaaS, web, móvil, IA. Desde 22 $/h. 65% ahorro.',
    },
    keywords: ['software development agency Bogotá', 'agencia desarrollo software Bogotá', 'IT outsourcing Colombia'],
    heroTitle: {
      en: 'Software Development Agency for Bogotá',
      fr: 'Agence de Développement Informatique pour Bogotá',
      es: 'Agencia de Desarrollo de Software para Bogotá',
    },
    heroSubtitle: {
      en: 'Bilingual engineers for Colombia\'s capital. US timezone overlap, 65% savings.',
      fr: 'Ingénieurs bilingues pour la capitale colombienne. 65% d\'économies.',
      es: 'Ingenieros bilingües para la capital colombiana. 65% ahorro.',
    },
    whyTitle: {
      en: 'Why Bogotá Companies Choose Call IT Dev',
      fr: 'Pourquoi les Entreprises de Bogotá Choisissent Call IT Dev',
      es: 'Por Qué las Empresas de Bogotá Eligen Call IT Dev',
    },
    whyReasons: [
      { icon: '🏙️', title: 'Capital Hub', description: 'Supporting Bogotá\'s thriving startup and enterprise tech ecosystem.' },
      { icon: '⏰', title: 'US Timezone', description: 'COT timezone — strong overlap with US East Coast.' },
      { icon: '💰', title: '65% Savings', description: 'Senior devs at $22-38/hr vs $80-150/hr in the US.' },
      { icon: '🗣️', title: 'Bilingual', description: 'Spanish and English fluency.' },
      { icon: '🛡️', title: 'Compliance', description: 'ISO 27001, GDPR, Ley 1581 compliant.' },
      { icon: '🚀', title: 'Fast Start', description: 'Team in 2 weeks.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Why outsource from Bogotá?', answer: 'Bogotá is LatAm\'s fastest-growing tech hub but faces talent demand. Our model extends your team at 65% savings.' },
      { question: 'Languages?', answer: 'Spanish and English bilingual engineers. French also available.' },
      { question: 'Technologies?', answer: 'React, TypeScript, Node.js, Python, Java, AWS, Azure, Docker, Kubernetes.' },
      { question: 'How fast?', answer: '2-week onboarding.' },
      { question: 'Data protection?', answer: 'ISO 27001, GDPR, Ley 1581 compliant.' },
      { question: 'Models?', answer: 'T&M, fixed-price, dedicated team.' },
    ],
    localRate: '$40-70',
    ourRate: '$22-38',
    savings: '65%+',
  },

  abuja: {
    slug: 'software-development-agency-abuja',
    cityName: 'Abuja',
    countryName: 'Nigeria',
    flag: '🇳🇬',
    timezone: 'WAT (GMT+1)',
    currency: 'NGN/USD',
    title: {
      en: 'Software Development Agency Abuja | Call IT Dev',
      fr: 'Agence de Développement Informatique Abuja | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Abuja, Nigeria. Custom SaaS, web, mobile, AI. English-speaking engineers, WAT timezone, from $20/hr.',
      fr: 'Agence de développement pour Abuja. SaaS, web, mobile, IA. Ingénieurs anglophones, fuseau WAT, dès 20 $/h.',
    },
    keywords: ['software development agency Abuja', 'IT outsourcing Abuja', 'software company Nigeria Abuja'],
    heroTitle: {
      en: 'Software Development Agency for Abuja',
      fr: 'Agence de Développement Informatique pour Abuja',
    },
    heroSubtitle: {
      en: 'English-speaking engineers for Nigeria\'s federal capital. WAT timezone, 75% savings.',
      fr: 'Ingénieurs anglophones pour la capitale fédérale du Nigeria. 75% d\'économies.',
    },
    whyTitle: {
      en: 'Why Abuja Companies Choose Call IT Dev',
      fr: 'Pourquoi les Entreprises d\'Abuja Choisissent Call IT Dev',
    },
    whyReasons: [
      { icon: '🏛️', title: 'Federal Capital', description: 'Supporting Abuja\'s government tech and enterprise digitization.' },
      { icon: '🗣️', title: 'English-Speaking', description: 'All engineers fluent in English.' },
      { icon: '⏰', title: 'WAT Timezone', description: 'Same timezone as CET for European collaboration.' },
      { icon: '💰', title: '75% Savings', description: 'Senior devs at $20-35/hr vs $80-120/hr globally.' },
      { icon: '🌍', title: 'Africa Expertise', description: 'Supporting Nigeria\'s digital transformation.' },
      { icon: '🚀', title: 'Scale Fast', description: 'Team in 2 weeks.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Why outsource from Abuja?', answer: 'Abuja is Nigeria\'s administrative capital with growing tech needs. Our model provides English-speaking engineers at 75% savings.' },
      { question: 'Technologies?', answer: 'React, TypeScript, Node.js, Python, Java, AWS, Azure, Docker, Kubernetes.' },
      { question: 'How fast?', answer: '2-week onboarding.' },
      { question: 'Security?', answer: 'ISO 27001 certified, GDPR compliant, NDPR aligned.' },
      { question: 'Models?', answer: 'T&M, fixed-price, dedicated team.' },
      { question: 'Scale?', answer: 'Start with 2 devs, scale to 30+.' },
    ],
    localRate: '$50-90',
    ourRate: '$20-35',
    savings: '75%+',
  },

  maputo: {
    slug: 'software-development-agency-maputo',
    cityName: 'Maputo',
    countryName: 'Mozambique',
    flag: '🇲🇿',
    timezone: 'CAT (GMT+2)',
    currency: 'MZN/USD',
    title: {
      en: 'Software Development Agency Maputo | Call IT Dev',
      fr: 'Agence de Développement Informatique Maputo | Call IT Dev',
      pt: 'Agência de Desenvolvimento de Software Maputo | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Maputo, Mozambique. Custom SaaS, web, mobile, AI. Portuguese-speaking engineers, from $18/hr.',
      fr: 'Agence de développement pour Maputo. SaaS, web, mobile, IA. Ingénieurs lusophones, dès 18 $/h.',
      pt: 'Agência de desenvolvimento para Maputo. SaaS, web, mobile, IA. Engenheiros lusófonos, a partir de 18 $/h.',
    },
    keywords: ['software development agency Maputo', 'IT outsourcing Mozambique', 'software company Maputo'],
    heroTitle: {
      en: 'Software Development Agency for Maputo',
      fr: 'Agence de Développement Informatique pour Maputo',
      pt: 'Agência de Desenvolvimento de Software para Maputo',
    },
    heroSubtitle: {
      en: 'Portuguese-speaking engineers for Mozambique\'s capital. CAT timezone, 80% savings.',
      fr: 'Ingénieurs lusophones pour la capitale mozambicaine. 80% d\'économies.',
      pt: 'Engenheiros lusófonos para a capital moçambicana. 80% de economia.',
    },
    whyTitle: {
      en: 'Why Maputo Companies Choose Call IT Dev',
      fr: 'Pourquoi les Entreprises de Maputo Choisissent Call IT Dev',
      pt: 'Por Que as Empresas de Maputo Escolhem Call IT Dev',
    },
    whyReasons: [
      { icon: '🌍', title: 'Lusophone Africa', description: 'Supporting Maputo\'s growing digital economy and PALOP market.' },
      { icon: '🗣️', title: 'Portuguese-Speaking', description: 'Engineers fluent in Portuguese for Mozambique market.' },
      { icon: '⏰', title: 'CAT Timezone', description: '1-hour difference from CET. Strong overlap.' },
      { icon: '💰', title: '80% Savings', description: 'Senior devs at $18-30/hr vs $80-120/hr globally.' },
      { icon: '🚀', title: 'Fast Start', description: 'Team in 2 weeks.' },
      { icon: '🛡️', title: 'Compliance', description: 'ISO 27001, GDPR compliant.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Why outsource from Maputo?', answer: 'Maputo is growing digitally but lacks senior tech talent. Our model provides Portuguese-speaking engineers at 80% savings.' },
      { question: 'Languages?', answer: 'Portuguese (native), English, French.' },
      { question: 'Technologies?', answer: 'React, TypeScript, Node.js, Python, Java, AWS, Azure, Docker, Kubernetes.' },
      { question: 'How fast?', answer: '2-week onboarding.' },
      { question: 'Security?', answer: 'ISO 27001 certified, GDPR compliant.' },
      { question: 'Models?', answer: 'T&M, fixed-price, dedicated team.' },
    ],
    localRate: '$50-90',
    ourRate: '$18-30',
    savings: '80%+',
  },

  muscat_hub2: {
    slug: 'software-development-agency-muscat-hub2',
    cityName: 'Muscat',
    countryName: 'Oman',
    flag: '🇴🇲',
    timezone: 'GST (GMT+4)',
    currency: 'OMR/USD',
    title: {
      en: 'Software Development Agency Muscat Hub | Call IT Dev',
      fr: 'Agence de Développement Informatique Mascate Hub | Call IT Dev',
      ar: 'وكالة تطوير برمجيات مسقط | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Muscat, Oman. Custom SaaS, web, mobile, AI. Arabic/English engineers, from $25/hr.',
      fr: 'Agence de développement pour Mascate. SaaS, web, mobile, IA. Dès 25 $/h.',
      ar: 'وكالة تطوير برمجيات لمسقط، عمان. SaaS، ويب، موبايل، ذكاء اصطناعي. من 25 $/ساعة.',
    },
    keywords: ['software development agency Muscat', 'IT outsourcing Oman', 'software company Muscat'],
    heroTitle: {
      en: 'Software Development Agency for Muscat',
      fr: 'Agence de Développement Informatique pour Mascate',
      ar: 'وكالة تطوير برمجيات لمسقط',
    },
    heroSubtitle: {
      en: 'Arabic/English engineers for Oman\'s Vision 2040. GST timezone, 65% savings.',
      fr: 'Ingénieurs arabe/anglais pour la Vision 2040 d\'Oman. 65% d\'économies.',
      ar: 'مهندسون عرب/إنجليز لرؤية عمان 2040. توفير 65%.',
    },
    whyTitle: {
      en: 'Why Muscat Companies Choose Call IT Dev',
      fr: 'Pourquoi les Entreprises de Mascate Choisissent Call IT Dev',
      ar: 'لماذا تختار شركات مسقط Call IT Dev',
    },
    whyReasons: [
      { icon: '🏛️', title: 'Vision 2040', description: 'Supporting Oman\'s digital transformation and diversification.' },
      { icon: '🗣️', title: 'Arabic/English', description: 'Engineers fluent in Arabic and English for GCC market.' },
      { icon: '⏰', title: 'GST Timezone', description: '3-hour difference. Good overlap for daily collaboration.' },
      { icon: '💰', title: '65% Savings', description: 'Senior devs at $25-40/hr vs $80-150/hr in Muscat.' },
      { icon: '🛡️', title: 'Compliance', description: 'ISO 27001, GDPR compliant.' },
      { icon: '🚀', title: 'Fast Start', description: 'Dedicated team in 2 weeks.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Why outsource from Muscat?', answer: 'Oman\'s Vision 2040 drives digital demand but local talent is limited. Our model provides Arabic/English engineers at 65% savings.' },
      { question: 'Languages?', answer: 'Arabic (native) and English. French also available.' },
      { question: 'Technologies?', answer: 'React, TypeScript, Node.js, Python, Java, AWS, Azure, Docker, Kubernetes.' },
      { question: 'How fast?', answer: '2-week onboarding.' },
      { question: 'Security?', answer: 'ISO 27001 certified, GDPR and Oman PDPL compliant.' },
      { question: 'Models?', answer: 'T&M, fixed-price, dedicated team.' },
    ],
    localRate: '$80-150',
    ourRate: '$25-40',
    savings: '65%+',
  },

};
